import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { Check } from "@mui/icons-material";

import { BrandedSplitLayout } from "../../../branding/components/BrandedSplitLayout";
import { BrandedFormHeader } from "../../../branding/components/BrandedFormHeader";
import { BrandedSecondaryAction } from "../../../branding/components/BrandedSecondaryAction";
import { BrandedResultMessage } from "../../../branding/components/BrandedResultMessage";
import { BrandedTextField } from "../../../branding/components/BrandedTextField";
import { BrandedButton } from "../../../branding/components/BrandedButton";
import { routesConfig } from "../../../config/routes";
import { useContextualNavigate } from "../../../hooks/ContextualNavigations";
import { useForgotPasswordSubmit } from "../ForgotPasswordDefault/hook/useForgotPasswordSubmit";

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Insira um e-mail válido")
    .required("Este campo é obrigatório"),
});

// Forgot Branded: view única responsiva (layout split). Sucesso inline via
// BrandedResultMessage com a copy original da Atem (sem {email}). Reusa o mesmo
// useForgotPasswordSubmit das views Default — nenhuma lógica de auth duplicada.
const ForgotPasswordBranded = (): JSX.Element => {
  const navigate = useContextualNavigate();
  const { submit } = useForgotPasswordSubmit();
  const [sent, setSent] = useState(false);

  const { errors, values, touched, isSubmitting, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: { email: "" },
      validationSchema,
      onSubmit: (vals) => submit(vals.email, () => setSent(true)),
    });

  const goToLogin = () => navigate(routesConfig.LOGIN);

  if (sent) {
    return (
      <BrandedSplitLayout>
        <BrandedResultMessage
          title="Confira seu e-mail"
          message="Nós enviamos instruções sobre uma nova senha para o seu e-mail"
          primaryLabel="Voltar para o login"
          onPrimary={goToLogin}
        />
      </BrandedSplitLayout>
    );
  }

  return (
    <BrandedSplitLayout>
      <BrandedFormHeader
        title="Redefinir senha"
        subtitle="Enviaremos instruções para a redefinição da sua senha"
      />

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <BrandedTextField
            type="email"
            id="email"
            name="email"
            label="E-mail"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Digite o email"
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <Check
                      sx={{ color: touched.email && !errors.email ? "success.main" : "transparent" }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <BrandedButton type="submit" loading={isSubmitting}>
            Continuar
          </BrandedButton>
        </Box>
      </form>

      <BrandedSecondaryAction actions={[{ label: "Voltar para o login", to: routesConfig.LOGIN }]} />
    </BrandedSplitLayout>
  );
};

export default ForgotPasswordBranded;
